import React, { useState, useEffect } from 'react'
import { X, Edit3, AlertCircle, Check } from 'lucide-react'
import { teamService } from './teamService'
import { DetailedRosterMember } from '../../types/team'

interface EditAthleteModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (message: string) => void
  member: DetailedRosterMember | null
}

const POSITIONS = ['QB', 'C', 'WR', 'SLOT', 'RUSH', 'LB', 'CB']

export const EditAthleteModal: React.FC<EditAthleteModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  member,
}) => {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [primaryPosition, setPrimaryPosition] = useState('WR')
  const [secondaryPositions, setSecondaryPositions] = useState<string[]>([])
  const [unit, setUnit] = useState<'offense' | 'defense' | 'both'>('offense')
  const [jerseyNumber, setJerseyNumber] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen || !member) {
      setErrorMsg(null)
      return
    }

    setFirstName(member.first_name || '')
    setLastName(member.last_name || '')
    setPrimaryPosition(member.primary_position || 'WR')
    setSecondaryPositions(member.secondary_positions || [])
    setUnit(member.unit || 'offense')
    setJerseyNumber(member.jersey_number !== null ? String(member.jersey_number) : '')
    setErrorMsg(null)

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = prevOverflow
    }
  }, [isOpen, member, onClose])

  if (!isOpen || !member) return null

  const handleToggleSecondaryPosition = (pos: string) => {
    if (pos === primaryPosition) return
    if (secondaryPositions.includes(pos)) {
      setSecondaryPositions(secondaryPositions.filter((p) => p !== pos))
    } else {
      setSecondaryPositions([...secondaryPositions, pos])
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMsg(null)

    if (!firstName.trim() || !lastName.trim()) {
      setErrorMsg('Nombre y apellidos son campos obligatorios.')
      return
    }

    setIsSubmitting(true)
    try {
      const res = await teamService.updateAthleteRoster({
        athleteId: member.athlete_id,
        teamId: member.team_id,
        seasonId: member.season_id,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        primaryPosition,
        secondaryPositions,
        unit,
        jerseyNumber: jerseyNumber ? parseInt(jerseyNumber, 10) : undefined,
      })

      if (res.success) {
        onSuccess(`Ficha de ${firstName} ${lastName} actualizada correctamente.`)
        onClose()
      }
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al actualizar la ficha.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="modal-content"
        style={{ maxWidth: '500px', width: '92vw' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Edit3 size={18} style={{ color: 'var(--brand-red)' }} />
            <h2 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>
              Editar Ficha Deportiva
            </h2>
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
            aria-label="Cerrar modal"
            style={{ padding: '0.25rem' }}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ maxHeight: '72vh', overflowY: 'auto' }}>
            {errorMsg && (
              <div
                style={{
                  padding: '0.75rem',
                  backgroundColor: 'var(--status-danger-bg)',
                  color: 'var(--status-danger-text)',
                  border: '1px solid var(--status-danger-border)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.82rem',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.5rem',
                }}
              >
                <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>{errorMsg}</div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                  Nombre *
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                  Apellidos *
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                  Posición Principal *
                </label>
                <select
                  className="form-control"
                  value={primaryPosition}
                  onChange={(e) => {
                    const newPos = e.target.value
                    setPrimaryPosition(newPos)
                    setSecondaryPositions(secondaryPositions.filter((p) => p !== newPos))
                  }}
                >
                  {POSITIONS.map((pos) => (
                    <option key={pos} value={pos}>
                      {pos}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                  Unidad Habitual *
                </label>
                <select
                  className="form-control"
                  value={unit}
                  onChange={(e) => setUnit(e.target.value as 'offense' | 'defense' | 'both')}
                >
                  <option value="offense">Ataque</option>
                  <option value="defense">Defensa</option>
                  <option value="both">Ambas (Polivalente)</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                Posiciones Secundarias
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {POSITIONS.filter((p) => p !== primaryPosition).map((pos) => {
                  const isChecked = secondaryPositions.includes(pos)
                  return (
                    <button
                      key={pos}
                      type="button"
                      className={`btn ${isChecked ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ padding: '0.25rem 0.55rem', fontSize: '0.75rem' }}
                      onClick={() => handleToggleSecondaryPosition(pos)}
                    >
                      {pos} {isChecked && <Check size={12} style={{ marginLeft: '4px' }} />}
                    </button>
                  )
                })}
              </div>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                Dorsal
              </label>
              <input
                type="number"
                min="0"
                max="99"
                className="form-control"
                placeholder="Sin dorsal"
                value={jerseyNumber}
                onChange={(e) => setJerseyNumber(e.target.value)}
              />
            </div>
          </div>

          <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '0.75rem 1rem' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
